import { useState } from "react";
import { addNotionBlock, updateNotionBlock, deleteNotionBlock } from "../../api";

// ── Editor del contenido (bloques) de una página de Notion ───────────────────
// Cada bloque se guarda de forma independiente al perder el foco: la API de
// Notion trabaja bloque a bloque, no con el documento entero.

const EDITABLE_TYPES = [
  { type: "paragraph",          label: "Texto" },
  { type: "heading_1",          label: "Título 1" },
  { type: "heading_2",          label: "Título 2" },
  { type: "heading_3",          label: "Título 3" },
  { type: "bulleted_list_item", label: "Lista" },
  { type: "numbered_list_item", label: "Lista numerada" },
  { type: "to_do",              label: "Tarea" },
  { type: "quote",              label: "Cita" },
  { type: "callout",            label: "Destacado" },
  { type: "toggle",             label: "Desplegable" },
];
const EDITABLE = new Set(EDITABLE_TYPES.map(t => t.type));

const TEXT_STYLES = {
  heading_1: { fontWeight: 800, fontSize: 16 },
  heading_2: { fontWeight: 700, fontSize: 14.5 },
  heading_3: { fontWeight: 700, fontSize: 13.5 },
  quote:     { fontStyle: "italic", color: "var(--text2)" },
};

function BlockRow({ block, onChange, onDelete, disabled }) {
  const [text, setText]   = useState(block.text || "");
  const [busy, setBusy]   = useState(false);
  const [hover, setHover] = useState(false);

  const save = async (patch = {}) => {
    const next = { type: block.type, text, checked: block.checked, ...patch };
    if (next.text === block.text && next.checked === block.checked) return;
    setBusy(true);
    try {
      const updated = await updateNotionBlock(block.id, next);
      onChange({ ...block, ...(updated || next) });
    } catch (e) {
      setText(block.text || "");           // revierte si Notion rechaza el cambio
      onChange(block, e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!EDITABLE.has(block.type)) {
    return (
      <div style={{ fontSize: 12.5, color: "var(--text3)", padding: "4px 0", display: "flex", gap: 6 }}>
        <span title={`Los bloques de tipo ${block.type} no se pueden editar desde aquí`}>🔒</span>
        {block.text || `[${block.type}]`}
      </div>
    );
  }

  const isList = block.type === "bulleted_list_item" || block.type === "numbered_list_item";

  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: "flex", alignItems: "flex-start", gap: 6, opacity: busy ? 0.6 : 1 }}>
      {block.type === "to_do" && (
        <input aria-label="Tarea hecha" type="checkbox" checked={!!block.checked} disabled={disabled}
          onChange={e => save({ checked: e.target.checked })}
          style={{ accentColor: "var(--accent)", width: 14, height: 14, marginTop: 6, flexShrink: 0 }} />
      )}
      {isList && <span style={{ color: "var(--text3)", marginTop: 5, flexShrink: 0 }}>•</span>}
      {block.type === "quote" && <span style={{ width: 3, alignSelf: "stretch", background: "var(--accent)", borderRadius: 2, flexShrink: 0 }} />}

      <textarea aria-label="Texto del bloque"
        rows={1}
        value={text}
        disabled={disabled}
        onChange={e => setText(e.target.value)}
        onBlur={e => {
          e.target.style.border = "1px solid transparent";
          e.target.style.background = "transparent";
          save();
        }}
        onInput={e => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
        style={{
          flex: 1, border: "1px solid transparent", background: "transparent",
          padding: "4px 6px", borderRadius: 6, resize: "none", overflow: "hidden",
          fontFamily: "var(--font)", fontSize: 13, lineHeight: 1.6, color: "var(--text)",
          textDecoration: block.type === "to_do" && block.checked ? "line-through" : "none",
          ...TEXT_STYLES[block.type],
        }}
        onFocus={e => { e.target.style.border = "1px solid var(--border)"; e.target.style.background = "var(--surface2)"; }}
      />

      <button onClick={onDelete} disabled={disabled}
        title="Eliminar bloque"
        style={{
          border: "none", background: "transparent", cursor: "pointer", color: "var(--text3)",
          fontSize: 13, padding: "4px 2px", visibility: hover ? "visible" : "hidden", flexShrink: 0,
        }}>✕</button>
    </div>
  );
}

export default function NotionBlockEditor({ pageId, blocks, setBlocks, onError, disabled }) {
  const [adding, setAdding]   = useState(false);
  const [newType, setNewType] = useState("paragraph");
  const [newText, setNewText] = useState("");

  const replaceBlock = (updated, error) => {
    if (error) return onError?.(error);
    setBlocks(bs => bs.map(b => (b.id === updated.id ? updated : b)));
  };

  const removeBlock = async (block) => {
    setBlocks(bs => bs.filter(b => b.id !== block.id));
    try {
      await deleteNotionBlock(block.id);
    } catch (e) {
      setBlocks(bs => [...bs, block]);      // restaura si Notion lo rechaza
      onError?.(e.message);
    }
  };

  const addBlock = async () => {
    if (!newText.trim()) return;
    setAdding(true);
    try {
      const created = await addNotionBlock(pageId, { type: newType, text: newText.trim(), checked: false });
      if (created) setBlocks(bs => [...bs, created]);
      setNewText("");
    } catch (e) {
      onError?.(e.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {blocks.length === 0 && (
        <p style={{ fontSize: 12.5, color: "var(--text3)", fontStyle: "italic", margin: "0 0 6px" }}>Sin contenido.</p>
      )}
      {blocks.map(b => (
        b.type === "divider"
          ? <hr key={b.id} style={{ border: "none", borderTop: "1px solid var(--border)", margin: "6px 0", width: "100%" }} />
          : <BlockRow key={b.id} block={b} disabled={disabled}
              onChange={replaceBlock} onDelete={() => removeBlock(b)} />
      ))}

      {/* Añadir bloque */}
      <div style={{ display: "flex", gap: 6, marginTop: 10, alignItems: "flex-start" }}>
        <select aria-label="Tipo de bloque nuevo" value={newType} onChange={e => setNewType(e.target.value)} disabled={disabled}
          style={{ padding: "6px 7px", borderRadius: 7, border: "1px solid var(--border-ctl)", background: "var(--surface2)", color: "var(--text)", fontSize: 12, fontFamily: "var(--font)" }}>
          {EDITABLE_TYPES.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
        </select>
        <input aria-label="Contenido del bloque nuevo" value={newText} disabled={disabled}
          placeholder="Añadir contenido…"
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addBlock(); } }}
          style={{ flex: 1, padding: "6px 9px", borderRadius: 7, border: "1px solid var(--border-ctl)", background: "var(--surface2)", color: "var(--text)", fontSize: 12.5, fontFamily: "var(--font)" }} />
        <button onClick={addBlock} disabled={disabled || adding || !newText.trim()}
          style={{
            padding: "6px 12px", borderRadius: 7, border: "1px solid var(--border)",
            background: newText.trim() ? "var(--accent)" : "var(--surface)",
            color: newText.trim() ? "#fff" : "var(--text3)",
            fontSize: 12, fontWeight: 600, cursor: newText.trim() ? "pointer" : "default", whiteSpace: "nowrap",
          }}>{adding ? "…" : "Añadir"}</button>
      </div>
    </div>
  );
}
